import Stack from '@mui/material/Stack'

import { Text } from '@mxenabled/mxui'
import { observer } from 'mobx-react-lite'

import {
  Drawer,
  Loader,
  useGlobalCopyStore,
  useGlobalUiStore,
  useInsightsV2Store,
} from '../../common'
import { type Beat } from '../../core'
import {
  actionBlocks,
  hasDrawerBlock,
  type ActionBlockEntry,
  type ActionBlockProps,
} from '../mappings/blocks'
import { getDynamicCtaBasedOnTriggers } from './util'

interface ActionBlockLayerProps {
  beat: Beat
}

/**
 * Renders a registered action block. Callers MUST key this per block name — a
 * block with `usePrepare` and one without run a different number of hooks, so
 * reusing the same fiber across a block switch violates the rules of hooks.
 */
const ActionBlockRunner = ({
  actionBlock,
  ...blockProps
}: ActionBlockProps & { actionBlock: ActionBlockEntry }) => {
  const { Component, usePrepare } = actionBlock

  usePrepare(blockProps)

  return <Component {...blockProps} />
}

// `observer` is required: this reads `isCopyLoaded` off GlobalUiStore and the
// action drawer off InsightsV2Store. Without it the component never re-renders
// and stays stuck on <Loader /> forever.
export const ActionBlockLayer = observer(function ActionBlockLayer({
  beat,
}: ActionBlockLayerProps) {
  const copy = useGlobalCopyStore()
  const commonCopy = copy.common
  const { isCopyLoaded } = useGlobalUiStore()
  const { actionDrawer, closeActionDrawer } = useInsightsV2Store()

  // Mirrors BaseLayout's guard — the layer is exported, so callers are not
  // guaranteed to have narrowed this already.
  if (!beat.insights_payload) return null

  const { primary_cta, secondary_cta, guid } = beat.insights_payload

  // Every card in the feed renders its own layer against the shared store, so
  // this card's drawer only opens when its beat is the one that triggered it.
  const isCurrentBeatOpen = actionDrawer.isDrawerOpen && actionDrawer.guid === guid

  if (!isCopyLoaded) return isCurrentBeatOpen ? <Loader /> : null

  const currentCta = getDynamicCtaBasedOnTriggers(actionDrawer, primary_cta, secondary_cta)

  // A CTA without a real block never opens a drawer (BaseLayout won't trigger it);
  // a real-but-unknown block shows the error ("oops") content.
  const blockValid = hasDrawerBlock(currentCta?.block)
  const actionBlock = blockValid
    ? actionBlocks[currentCta?.block as keyof typeof actionBlocks]
    : undefined

  const drawerTitle = actionBlock
    ? (actionBlock.title?.(currentCta?.variant, copy) ?? currentCta?.label ?? '')
    : blockValid
      ? commonCopy.error_heading
      : (currentCta?.label ?? '')

  return (
    <Drawer
      ariaLabelClose={commonCopy.close_aria}
      isOpen={isCurrentBeatOpen}
      onClose={closeActionDrawer}
      title={drawerTitle}
    >
      {actionBlock ? (
        <ActionBlockRunner actionBlock={actionBlock} beat={beat} key={currentCta?.block} />
      ) : blockValid ? (
        <Stack sx={{ p: 24 }}>
          <Text>{commonCopy.error_message}</Text>
        </Stack>
      ) : null}
    </Drawer>
  )
})
